<template>
  <div
    id="register"
    class="w-full max-w-[52rem] mx-auto px-4 sm:px-6 pt-8 lg:pt-12 pb-20 lg:pb-28"
  >
    <h1 class="page-title">컨퍼런스 신청</h1>

    <!-- 신청 컨퍼런스 -->
    <section class="conference-summary" aria-label="신청 컨퍼런스">
      <span v-if="state.conferences.conferenceDetail.dept" class="dept-badge">
        {{ state.conferences.conferenceDetail.dept }}
      </span>
      <p class="summary-title">
        {{ state.conferences.conferenceDetail.conference.title }}
      </p>
      <ul class="summary-meta">
        <li v-if="placeText">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.8"
            stroke="currentColor"
            class="summary-meta-icon"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
            />
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
            />
          </svg>
          {{ placeText }}
        </li>
        <li v-if="state.conferences.conferenceDetail.schedule">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.8"
            stroke="currentColor"
            class="summary-meta-icon"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
            />
          </svg>
          {{ formattedDate }}
        </li>
      </ul>
    </section>

    <!-- 신청자 정보 -->
    <section class="form-section" aria-labelledby="applicant-title">
      <h2 id="applicant-title" class="section-title">신청자 정보</h2>

      <div class="field-list">
        <div class="field-row">
          <label for="applicant-name" class="field-label">신청자 이름</label>
          <input
            id="applicant-name"
            type="text"
            class="input"
            v-model="state.conferences.member.name"
            disabled
          />
        </div>

        <div class="field-row field-row--top">
          <span id="phone-label" class="field-label">
            연락처 <span class="required" aria-hidden="true">*</span>
          </span>
          <div>
            <div
              class="phone-group mont"
              role="group"
              aria-labelledby="phone-label"
            >
              <input
                type="text"
                inputmode="numeric"
                v-model="state.conferences.member.phone1"
                maxlength="3"
                class="input text-center"
                aria-label="연락처 앞자리"
                :disabled="state.appleJoinCheck === false"
              />
              <span class="text-gray-400" aria-hidden="true">-</span>
              <input
                type="text"
                inputmode="numeric"
                v-model="state.conferences.member.phone2"
                maxlength="4"
                class="input text-center"
                aria-label="연락처 가운데 자리"
                :disabled="state.appleJoinCheck === false"
              />
              <span class="text-gray-400" aria-hidden="true">-</span>
              <input
                type="text"
                inputmode="numeric"
                v-model="state.conferences.member.phone3"
                maxlength="4"
                class="input text-center"
                aria-label="연락처 끝자리"
                :disabled="state.appleJoinCheck === false"
              />
            </div>
            <div class="field-notice">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.8"
                stroke="currentColor"
                class="field-notice-icon"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
                />
              </svg>
              <p>
                위 연락처로 <b>중요 공지문자</b>가 전송되오니 정확히
                기재바랍니다. 입력한 연락처는 개인연락처와 동기화됩니다. 만약
                본인 연락처가 아니신 경우
                <router-link to="/profile" class="underline underline-offset-2">
                  마이페이지</router-link
                >에서 본인의 연락처로 업데이트를 부탁드립니다.
              </p>
            </div>
          </div>
        </div>

        <div class="field-row">
          <label for="applicant-email" class="field-label">이메일</label>
          <input
            id="applicant-email"
            type="text"
            class="input"
            v-model="state.conferences.member.email"
            disabled
          />
        </div>

        <div class="field-row">
          <label for="applicant-church" class="field-label">
            교회명 <span class="required" aria-hidden="true">*</span>
          </label>
          <input
            id="applicant-church"
            type="text"
            class="input"
            placeholder="교회명을 입력하세요"
            v-model="formData.registerConference.church"
          />
        </div>

        <div
          class="field-row field-row--top"
          v-if="state.conferences.conferenceDetail.conference.isAddress === 'Y'"
        >
          <span class="field-label">주소</span>
          <div class="space-y-2">
            <div class="flex gap-2">
              <input
                type="text"
                class="input input--zip"
                placeholder="우편번호"
                aria-label="우편번호"
                maxlength="5"
                v-model="formData.zipCode"
                readonly
                @focus="searchAddress"
              />
              <button
                type="button"
                class="secondary-button"
                @click="searchAddress"
              >
                주소 검색
              </button>
            </div>
            <input
              type="text"
              class="input"
              placeholder="기본주소"
              aria-label="기본주소"
              v-model="formData.address"
              readonly
              @focus="searchAddress"
            />
            <input
              type="text"
              class="input"
              placeholder="상세주소"
              aria-label="상세주소"
              v-model="formData.detailedAddress"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- 참석자 정보 -->
    <section class="form-section" aria-labelledby="attendee-title">
      <div class="flex items-baseline justify-between gap-3">
        <h2 id="attendee-title" class="section-title">
          참석자 정보 <span class="required" aria-hidden="true">*</span>
        </h2>
        <span class="text-sm text-gray-500">
          총
          <b class="font-bold text-main-navy">{{
            formData.registerMemberList.length
          }}</b
          >명
        </span>
      </div>

      <p class="section-notice">
        ※ <b>신청자</b>는 <b>참석자</b>에 포함되지 않습니다. 신청자도 참석하는
        경우 참석자 정보에 입력(추가)해 주세요.
      </p>

      <ol class="mt-5 space-y-2.5">
        <li
          v-for="(item, index) in formData.registerMemberList"
          :key="index"
          class="attendee-row"
        >
          <span class="attendee-index mont">{{ index + 1 }}</span>
          <div class="grid min-w-0 grow basis-0 grid-cols-2 gap-2">
            <input
              type="text"
              class="input"
              placeholder="이름"
              :aria-label="`참석자 ${index + 1} 이름`"
              v-model="item.name"
            />
            <input
              type="text"
              class="input"
              placeholder="교회명"
              :aria-label="`참석자 ${index + 1} 교회명`"
              v-model="item.church"
            />
          </div>
          <button
            v-if="formData.registerMemberList.length > 1"
            type="button"
            class="icon-button"
            :aria-label="`참석자 ${index + 1} 삭제`"
            @click="removeItem(index)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="2"
              stroke="currentColor"
              class="w-5 h-5"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          </button>
        </li>
      </ol>

      <button type="button" class="add-button" @click="addItem">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2"
          stroke="currentColor"
          class="w-4 h-4"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 4.5v15m7.5-7.5h-15"
          />
        </svg>
        참석자 추가
      </button>
    </section>

    <!-- 등록비 -->
    <section class="form-section" aria-labelledby="price-title">
      <h2 id="price-title" class="section-title">등록비</h2>

      <div class="field-list">
        <div class="field-row">
          <label for="price" class="field-label">등록비 (1인)</label>
          <div class="amount-field">
            <input
              id="price"
              type="text"
              class="input input--amount"
              :value="formatPrice(state.conferences.conferenceDetail.price)"
              readonly
            />
            <span class="amount-unit">원</span>
          </div>
        </div>

        <div class="field-row field-row--top">
          <label for="total-price" class="field-label md:pt-3.5">
            등록비 합계
          </label>
          <div>
            <div class="amount-field">
              <input
                id="total-price"
                type="text"
                class="input input--amount input--total"
                :value="formatPrice(formData.registerConference.total_price)"
                readonly
              />
              <span class="amount-unit">원</span>
            </div>
            <p class="mt-2 text-sm text-gray-500">
              {{ formatPrice(state.conferences.conferenceDetail.price) }}원 ×
              {{ formData.registerMemberList.length }}명
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- 개인정보 수집·이용 동의 -->
    <section class="form-section" aria-labelledby="policy-title">
      <h2 id="policy-title" class="section-title">개인정보 수집·이용 동의</h2>

      <div class="policy-box" tabindex="0">
        <p class="font-semibold text-gray-800">[개인정보의 수집에 대한 사항]</p>
        <p>
          대한예수교장로회총회 총회교육개발원은 이용자의 개인정보를 중요시하며,
          『개인정보보호법』 제15조 및 제 22조에 따라 다음과 같은 내용의
          개인정보 수집 및 이용을 하고자 합니다. 총회교육개발원은 이
          「정보통신망법」을 비롯하여 개인정보와 관련된 법령 상의
          개인정보보호규정 및 방송통신위원회가 제정한 "개인정보보호지침"을
          준수하고 있습니다.
        </p>
        <ul class="policy-list">
          <li><b>개인정보의 수집·이용 목적</b> : 컨퍼런스 신청모집 및 안내</li>
          <li>
            <b>수집하는 개인정보의 항목</b> : 성함, 연락처, 이메일, 교회명
          </li>
          <li><b>개인정보의 보유·이용 기간</b> : 모집 종료 후 1년</li>
        </ul>
        <p class="font-semibold text-gray-800">[동의여부]</p>
        <p>
          개인정보의 수집 및 이용에 대한 동의를 거부할 수 있으며, 이 경우 신청이
          제한됩니다.
        </p>
      </div>

      <label for="policy" class="agree-check">
        <input
          id="policy"
          type="checkbox"
          name="policy"
          class="h-5 w-5 shrink-0 cursor-pointer accent-main-navy"
          v-model="state.agreeCheck"
        />
        <span>
          개인정보 수집 및 이용에 동의합니다.
          <span class="text-main-navy">(필수)</span>
        </span>
      </label>
    </section>

    <button type="button" class="submit-button" @click="handleSubmit">
      신청하기
    </button>
  </div>

  <div id="addressFrame" style="display: none">
    <img
      src="//t1.kakaocdn.net/zipCode/resource/images/close.png"
      id="btnFoldWrap"
      style="
        cursor: pointer;
        position: absolute;
        right: 0px;
        top: -1px;
        z-index: 1;
      "
      @click="foldAddress()"
      alt="접기 버튼"
    />
  </div>
</template>

<script>
import { useRoute, useRouter } from "vue-router";
import { reactive, computed, onMounted, watchEffect } from "vue";
import axios from "axios";
import store from "@/store";
import { numberWithCommas } from "@/assets/js/util.js";

const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

export default {
  name: "EduRegisterPage",

  setup() {
    const route = useRoute();
    const router = useRouter();

    const state = reactive({
      conferences: {
        member: {
          phone: "",
          phone1: "",
          phone2: "",
          phone3: "",
        },
        conferenceDetail: {
          price: 0,
          local: "",
          place: "",
          limit_personnel: 0,
          registered_personnel: 0,

          conference: {
            idx: 0,
            title: "",
            rsdatetime: "",
            redatetime: "",
          },
        },
      },

      agreeCheck: false,
      appleJoinCheck: false,
    });

    const viewDetails = reactive({
      value: false,
    });

    const toggleView = () => {
      viewDetails.value = !viewDetails.value;
    };

    const formData = reactive({
      zipCode: "",
      address: "",
      detailedAddress: "",
      registerConference: {
        name: "",
        phone: "",
        email: "",
        church: "",
        total_price: 0,
      },
      registerMemberList: [
        {
          name: "",
          church: "",
        },
      ],
    });

    // 컨퍼런스 세부 idx
    const idx = route.params.idx;

    //신청자 기본 정보 get
    onMounted(() => {
      axios
        .get("/api/register/conference/" + idx)
        .then((response) => {
          if (response.data.err_code == 0) {
            isAvailable(state.conferences);
            state.conferences = response.data.contents;

            console.log(state.conferences.member);
            // state.appleJoinCheck = state.conferences.member.apple === true;
            state.appleJoinCheck =
              (state.conferences.member.phone === "" ||
                state.conferences.member.phone === null) &&
              state.conferences.member.apple === true;
            /*let price = parseInt(state.conferences.conferenceDetail.price);
          price = price.toLocaleString('ko-KR');
          state.conferences.conferenceDetail.price_txt = price;*/
            state.conferences.member.phone1 =
              state.conferences.member.phone.slice(0, 3);
            state.conferences.member.phone2 =
              state.conferences.member.phone.slice(3, 7);
            state.conferences.member.phone3 =
              state.conferences.member.phone.slice(7, 11);

            formData.registerConference.name = state.conferences.member.name;
            formData.registerConference.phone = state.conferences.member.phone;
            formData.registerConference.email = state.conferences.member.email;
          } else {
            alert(response.data.err_msg);
            router.push({ path: "/" });
          }
        })
        .catch((error) => {
          //로그인 안 되어있을 시
          if (!store.state.account.id) {
            alert("로그인 후 이용해주세요.");
            router.push({ path: "/" });
          } else {
            console.error(error);
            router.push({ path: "/" });
            // document.location.href = '/';
          }
        });
    });

    //접수기간 체크
    const isAvailable = (conferences) => {
      const { rsdatetime, redatetime } =
        conferences.conferenceDetail.conference;
      const today = new Date();

      const startDate = new Date(Date.parse(rsdatetime));
      const endDate = new Date(Date.parse(redatetime));

      if (today < startDate || today > endDate) {
        alert("접수 기간이 아닙니다. 이전 페이지로 돌아갑니다.");
        window.history.back();
      }
    };

    //타이틀 날짜 format - 2026년 10월 31일 (토) 오전 10시
    const formattedDate = computed(() => {
      const date = new Date(state.conferences.conferenceDetail.schedule);
      const hours = date.getHours();
      const minutes = date.getMinutes();

      return `${date.getFullYear()}년 ${
        date.getMonth() + 1
      }월 ${date.getDate()}일 (${WEEKDAYS[date.getDay()]}) ${
        hours < 12 ? "오전" : "오후"
      } ${hours % 12 || 12}시${minutes ? ` ${minutes}분` : ""}`;
    });

    // 지역 · 장소
    const placeText = computed(() => {
      const { local, place } = state.conferences.conferenceDetail;
      return [local, place]
        .map((text) => (text ?? "").trim())
        .filter(Boolean)
        .join(" · ");
    });

    // 금액 1,000 단위 구분
    const formatPrice = (price) => numberWithCommas(Number(price) || 0);

    const searchAddress = () => {
      const addressFrame = document.querySelector("#addressFrame");

      new window.daum.Postcode({
        oncomplete: (data) => {
          console.log(data);
          formData.zipCode = data.zonecode;
          formData.address = data.roadAddress;
          addressFrame.style.display = "none";
        },
        // }).open();
      }).embed(addressFrame);

      addressFrame.style.display = "block";

      initLayerPosition();
    };

    const foldAddress = () => {
      document.querySelector("#addressFrame").style.display = "none";
    };

    // 스크롤 위치와 상관없이 화면 가운데에, 모바일에서는 화면 폭에 맞춰 띄운다
    const initLayerPosition = () => {
      // box-sizing: border-box 라 width/height에 테두리가 포함된다
      const borderWidth = 5;
      const width = Math.min(520, window.innerWidth - 32);
      const height = Math.min(520, window.innerHeight - 32);
      const addressFrame = document.querySelector("#addressFrame");
      const offsetLeft = (window.innerWidth - width) / 2;
      const offsetTop = (window.innerHeight - height) / 2;

      addressFrame.style.position = "fixed";
      addressFrame.style.zIndex = "50";
      addressFrame.style.background = "#fff";
      addressFrame.style.width = width + "px";
      addressFrame.style.height = height + "px";
      addressFrame.style.border = borderWidth + "px solid";
      addressFrame.style.left = offsetLeft + "px";
      addressFrame.style.top = offsetTop + "px";
    };

    const handleSubmit = async () => {
      /*console.log(formData);
      return false;*/
      formData.registerConference.name = state.conferences.member.name;
      formData.registerConference.phone =
        state.conferences.member.phone1 +
        "" +
        state.conferences.member.phone2 +
        "" +
        state.conferences.member.phone3;
      formData.registerConference.email = state.conferences.member.email;
      const { registerConference, registerMemberList } = formData;
      const { conferenceDetail, member } = state.conferences;
      const { limit_personnel, registered_personnel } = conferenceDetail;

      if (!registerConference.name) {
        alert("신청자 이름을 입력해 주세요.");
        return;
      }

      // 핸드폰 번호 유효성 검사
      const phoneReg = /^0[0-9]{2}[0-9]{4}[0-9]{4}$/;
      if (!phoneReg.test(registerConference.phone)) {
        alert("올바른 핸드폰 번호를 입력해주세요.");
        return;
      }

      if (!registerConference.church) {
        alert("교회명을 기입해주세요.");
        return;
      }

      for (const attendee of registerMemberList) {
        if (!attendee.name || !attendee.church) {
          alert("참석자 정보를 모두 기입해주세요.");
          return;
        }
      }

      if (!state.agreeCheck) {
        alert("약관을 동의해주세요.");
        return;
      }

      /*console.log(limit_personnel);
      console.log(registered_personnel);
      console.log(registerMemberList.length);
      console.log( limit_personnel - registered_personnel )
      console.log(limit_personnel - registered_personnel < registerMemberList.length)*/

      if (
        limit_personnel - registered_personnel < registerMemberList.length &&
        limit_personnel != 0
      ) {
        alert("신청 가능 인원이 초과되었습니다.");
        return;
      }

      const url = `/api/payment/check-limit/${state.conferences.conferenceDetail.idx}?headcount=${registerMemberList.length}`;
      const response = await axios.get(url);
      if (response.data.err_code === 0) {
        try {
          const response = await axios.post(
            "/api/register/conference/" + idx,
            formData,
          );
          if (response.data.err_code === 0) {
            const appCheck = thisPageName === "eduRegister2App" ? "/app" : "";
            await router.push(
              appCheck + "/register_pay/" + response.data.contents,
            );
          } else {
            alert(response.data.err_msg);
          }
        } catch (error) {
          console.error(error);
        }
      } else if (response.data.err_code === 403) {
        alert(response.data.err_msg);
        return false;
      } else {
        alert(response.data.err_msg);
        return false;
      }
    };

    // 참석자 추가,삭제 & 인원수 체크
    const addItem = () => {
      const { limit_personnel, registered_personnel } =
        state.conferences.conferenceDetail;
      if (
        limit_personnel != 0 &&
        limit_personnel - registered_personnel <=
          formData.registerMemberList.length
      ) {
        alert("신청 가능 인원이 초과되었습니다.");
      } else {
        formData.registerMemberList.push({
          name: "",
          church: "",
        });
      }
      console.log(limit_personnel - registered_personnel);
      console.log(formData.registerMemberList.length);
    };

    const removeItem = (index) => {
      formData.registerMemberList.splice(index, 1);
    };

    // 등록비 합계
    // const updateTotalPrice = () => {
    //   formData.registerConference.total_price = state.conferences.conferenceDetail.price * (formData.registerMemberList.length + 1)

    //   formData.registerConference.total_price = Number(
    //     formData.registerConference.total_price
    //   )

    //   console.log(state.conferences.conferenceDetail.price)
    //   console.log(formData.registerMemberList)
    //   console.log(formData.registerConference.total_price)
    // }

    const updateTotalPrice = () => {
      formData.registerConference.total_price =
        state.conferences.conferenceDetail.price *
        formData.registerMemberList.length;

      formData.registerConference.total_price = Number(
        formData.registerConference.total_price,
      );
      // console.log(state.conferences.conferenceDetail.price)
      // console.log(formData.registerMemberList.length)
      // console.log(formData.registerConference.total_price)
    };

    watchEffect(() => {
      updateTotalPrice();
    });

    const thisPageName = route.name;

    return {
      state,
      viewDetails,
      toggleView,
      handleSubmit,
      formData,
      formattedDate,
      placeText,
      formatPrice,
      addItem,
      removeItem,
      updateTotalPrice,
      searchAddress,
      foldAddress,
      thisPageName,
    };
  },
};
</script>

<style scoped>
.detail_view {
  position: relative;
}
.detail_view::before {
  content: "";
  display: block;
  width: 100%;
  height: 50px;
  background: rgb(255, 255, 255);
  background: linear-gradient(
    0deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 0) 100%
  );
  position: absolute;
  top: -50px;
}

.page-title {
  @apply text-2xl md:text-[2rem] font-bold tracking-tight text-gray-900;
}

.required {
  @apply text-red-500;
}

/* 신청 컨퍼런스 */
.conference-summary {
  @apply mt-6 rounded-2xl bg-slate-50 p-5 md:p-7;
}

.dept-badge {
  @apply inline-flex items-center rounded-full bg-main-navy px-3 py-1 text-xs font-semibold text-white;
}

.summary-title {
  @apply mt-3 text-lg md:text-xl font-bold leading-snug text-gray-900 break-keep;
}

.summary-meta {
  @apply mt-3 flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-1.5 text-base text-gray-800;
}

.summary-meta li {
  @apply flex items-center gap-1.5;
}

.summary-meta-icon {
  @apply h-[1.125rem] w-[1.125rem] shrink-0 text-gray-400;
}

/* 섹션 */
.form-section {
  @apply mt-6 rounded-2xl border border-gray-200 bg-white p-5 md:p-8;
}

.section-title {
  @apply text-lg md:text-xl font-bold tracking-tight text-gray-900;
}

.section-notice {
  @apply mt-3 rounded-xl bg-slate-50 px-4 py-3 text-sm leading-relaxed text-gray-600 break-keep;
}

.section-notice b {
  @apply font-semibold text-gray-900;
}

/* 입력 항목 */
.field-list {
  @apply mt-6 space-y-5;
}

.field-row {
  @apply grid gap-2 md:grid-cols-[8.5rem_minmax(0,1fr)] md:items-center md:gap-6;
}

.field-row--top {
  @apply md:items-start;
}

.field-row--top > .field-label {
  @apply md:pt-3.5;
}

.field-label {
  @apply text-sm md:text-[0.9375rem] font-semibold text-gray-700;
}

.input {
  @apply h-12 w-full min-w-0 rounded-xl border border-gray-300 bg-white px-4 text-[0.9375rem] text-gray-900 transition placeholder:text-gray-400 focus:border-main-navy focus:outline-none focus:ring-4 focus:ring-main-navy/10 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-800;
}

.input--zip {
  @apply w-36 shrink-0;
}

.phone-group {
  @apply grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 md:max-w-[22rem];
}

.field-notice {
  @apply mt-2.5 flex items-start gap-2 text-[0.8125rem] leading-relaxed text-gray-500 break-keep;
}

.field-notice b {
  @apply font-semibold text-red-500;
}

.field-notice-icon {
  @apply mt-0.5 h-4 w-4 shrink-0 text-gray-400;
}

.secondary-button {
  @apply inline-flex h-12 shrink-0 items-center justify-center rounded-xl border border-main-navy px-5 text-sm font-semibold text-main-navy transition-colors hover:bg-main-navy hover:text-white;
}

/* 참석자 */
.attendee-row {
  @apply flex items-center gap-2 md:gap-3;
}

.attendee-index {
  @apply flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-gray-600;
}

.icon-button {
  @apply flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500;
}

.add-button {
  @apply mt-4 flex h-12 w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-gray-300 text-[0.9375rem] font-semibold text-gray-600 transition-colors hover:border-main-navy hover:bg-slate-50 hover:text-main-navy;
}

/* 등록비 */
.amount-field {
  @apply relative md:max-w-[16rem];
}

.input--amount {
  @apply pr-10 text-right font-semibold tabular-nums;
}

.input--amount[readonly] {
  @apply cursor-default border-gray-200 bg-gray-50 focus:border-gray-200 focus:ring-0;
}

.input--total {
  @apply text-lg font-bold text-main-navy;
}

.amount-unit {
  @apply pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[0.9375rem] text-gray-500;
}

/* 개인정보 동의 */
.policy-box {
  @apply mt-5 max-h-56 space-y-3 overflow-y-auto rounded-xl border border-gray-200 bg-slate-50 p-4 md:p-5 text-sm leading-relaxed text-gray-600 break-keep focus:outline-none focus-visible:ring-2 focus-visible:ring-main-navy;
}

.policy-list {
  @apply list-disc space-y-1 pl-5;
}

.policy-list b {
  @apply font-semibold text-gray-800;
}

.agree-check {
  @apply mt-4 flex cursor-pointer items-center gap-3 rounded-xl border border-gray-200 px-4 py-3.5 text-[0.9375rem] font-medium text-gray-800 transition-colors hover:bg-slate-50 has-[:checked]:border-main-navy has-[:checked]:bg-main-navy/5;
}

.submit-button {
  @apply mt-8 flex h-14 w-full items-center justify-center rounded-xl bg-main-navy text-base md:text-lg font-bold text-white shadow-sm transition-colors hover:bg-main-navy/90 focus:outline-none focus-visible:ring-4 focus-visible:ring-main-navy/30;
}
</style>
